import { Injectable, inject, PLATFORM_ID, isDevMode } from '@angular/core';
import { isPlatformBrowser, DOCUMENT, Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class KioskService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly platformId = inject(PLATFORM_ID);
  private isFullscreen = false;
  private readonly isDevelopment = isDevMode();
  private navigationBlocked = false;

  /**
   * Initialize kiosk mode - call this in app component
   */
  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Prevent browser back/forward navigation
    this.preventBrowserNavigation();
  }

  /**
   * Prevent right-click context menu
   */
  private preventContextMenu(): void {
    this.document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    }, false);

    // Also prevent long press on touch devices
    this.document.addEventListener('touchstart', (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    }, { passive: false });

    this.document.addEventListener('touchend', (e) => {
      const now = Date.now();
      const timeSinceTouchStart = now - (e.timeStamp || now);
      if (timeSinceTouchStart > 500) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Prevent browser back/forward navigation
   */
  private preventBrowserNavigation(): void {
    if (this.navigationBlocked) {
      return; // Already initialized
    }
    this.navigationBlocked = true;

    // Replace current state to prevent back navigation
    // Use replaceState instead of pushState to avoid building history
    const currentUrl = window.location.href;
    history.replaceState(null, '', currentUrl);
    
    // Add a dummy state to the history stack
    history.pushState({ preventBack: true }, '', currentUrl);

    // Prevent back button - intercept popstate events with aggressive handling
    const handlePopState = (e: PopStateEvent) => {
      // Always push the current state back to prevent navigation
      history.pushState({ preventBack: true }, '', window.location.href);
      
      // Prevent the default behavior
      e.preventDefault();
      e.stopImmediatePropagation();
      
      // Force stay on current page
      window.location.hash = '';
    };

    // Use capture phase to catch the event early
    window.addEventListener('popstate', handlePopState, true);
    
    // Also use beforeunload as additional protection
    window.addEventListener('beforeunload', (e) => {
      // This will show a confirmation dialog, but we can prevent it
      // For kiosk mode, we might want to prevent navigation entirely
    });

    // Prevent on each route change - replace state instead of push
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // Replace state after each navigation to prevent back button
        setTimeout(() => {
          history.replaceState({ preventBack: true }, '', window.location.href);
          history.pushState({ preventBack: true }, '', window.location.href);
        }, 0);
      });

    // Prevent backspace key from navigating back (except in input fields)
    const handleKeyDown = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || 
                      target.tagName === 'TEXTAREA' || 
                      target.isContentEditable ||
                      (target as any).contentEditable === 'true';
      
      // Prevent backspace from navigating back when not in input fields
      if (e.key === 'Backspace' && !isInput) {
        // Check if focus is on body or document
        if (document.activeElement === document.body || 
            document.activeElement === document.documentElement) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
      
      // Prevent Alt+Left Arrow (browser back shortcut)
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    this.document.addEventListener('keydown', handleKeyDown, true);

    // Additional protection: Monitor hash changes
    window.addEventListener('hashchange', (e): void => {
      e.preventDefault();
      e.stopImmediatePropagation();
      history.pushState({ preventBack: true }, '', window.location.href);
    }, true);

    // Override history.back, history.forward, history.go
    history.back = () => {
      // Do nothing - prevent back navigation
      history.pushState({ preventBack: true }, '', window.location.href);
    };

    history.forward = () => {
      // Do nothing - prevent forward navigation
      history.pushState({ preventBack: true }, '', window.location.href);
    };

    history.go = (delta?: number) => {
      if (delta && delta !== 0) {
        // Prevent going back or forward
        history.pushState({ preventBack: true }, '', window.location.href);
      }
    };
  }

  /**
   * Prevent common keyboard shortcuts that could exit kiosk mode
   */
  private preventKeyboardShortcuts(): void {
    this.document.addEventListener('keydown', (e) => {
      const devToolsEnabled = this.isDevToolsEnabled();
      
      if (this.shouldAllowDevTools(e, devToolsEnabled)) {
        return;
      }

      if (this.shouldPreventShortcut(e, devToolsEnabled)) {
        e.preventDefault();
      }
    }, true);
  }

  private isDevToolsEnabled(): boolean {
    return this.isDevelopment || 
      (typeof localStorage !== 'undefined' && localStorage.getItem('devToolsEnabled') === 'true');
  }

  private shouldAllowDevTools(e: KeyboardEvent, devToolsEnabled: boolean): boolean {
    if (!devToolsEnabled) {
      return false;
    }
    if (e.key === 'F12') {
      return true;
    }
    if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) {
      return true;
    }
    return (e.metaKey && e.altKey && (e.key === 'I' || e.key === 'J'));
  }

  private shouldPreventShortcut(e: KeyboardEvent, devToolsEnabled: boolean): boolean {
    // Prevent F11 (fullscreen toggle)
    if (e.key === 'F11') {
      return true;
    }

    // Prevent dev tools shortcuts if not enabled
    if (!devToolsEnabled && this.isDevToolsShortcut(e)) {
      return true;
    }

    // Prevent browser shortcuts
    return this.isBrowserShortcut(e);
  }

  private isDevToolsShortcut(e: KeyboardEvent): boolean {
    return e.key === 'F12' ||
      (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'));
  }

  private isBrowserShortcut(e: KeyboardEvent): boolean {
    return (e.ctrlKey && (e.key === 'w' || e.key === 'W')) ||
      (e.ctrlKey && (e.key === 't' || e.key === 'T')) ||
      (e.altKey && e.key === 'F4');
  }

  /**
   * Prevent text selection (optional for kiosk mode)
   */
  private preventTextSelection(): void {
    this.document.addEventListener('selectstart', (e) => {
      // Allow selection in input fields
      const target = e.target as HTMLElement;
      if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
        e.preventDefault();
      }
    }, false);

    // Prevent drag
    this.document.addEventListener('dragstart', (e) => {
      e.preventDefault();
    }, false);
  }

  /**
   * Enter fullscreen mode
   */
  enterFullscreen(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const elem = this.document.documentElement;

    if (elem.requestFullscreen) {
      elem.requestFullscreen().then(() => {
        this.isFullscreen = true;
      }).catch(() => {
        // Fullscreen request failed, continue anyway
        console.warn('Fullscreen request failed');
      });
    } else if ((elem as any).webkitRequestFullscreen) {
      // Safari
      (elem as any).webkitRequestFullscreen();
      this.isFullscreen = true;
    } else if ((elem as any).msRequestFullscreen) {
      // IE/Edge
      (elem as any).msRequestFullscreen();
      this.isFullscreen = true;
    }
  }

  /**
   * Exit fullscreen mode
   */
  exitFullscreen(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.document.exitFullscreen) {
      this.document.exitFullscreen();
    } else if ((this.document as any).webkitExitFullscreen) {
      (this.document as any).webkitExitFullscreen();
    } else if ((this.document as any).msExitFullscreen) {
      (this.document as any).msExitFullscreen();
    }
    this.isFullscreen = false;
  }

  /**
   * Handle visibility change (when user tries to switch tabs/apps)
   */
  private handleVisibilityChange(): void {
    this.document.addEventListener('visibilitychange', () => {
      if (this.document.hidden) {
        // User switched away - you could log this or take action
        return;
      }
      // User came back - ensure we're still in fullscreen
      if (!this.isFullscreen) {
        this.enterFullscreen();
      }
    });
  }

  /**
   * Get current fullscreen state
   */
  getIsFullscreen(): boolean {
    return this.isFullscreen;
  }
}


